// Layout-oriented public-page blocks: mapping table, icon-chip grid,
// stacked feature list, inline highlight, titled frame, section divider,
// footer callout. All share the same shape: container + inner nodes, with
// rich content where possible so the editor stays usable without custom
// NodeViews.
//
// Icons live in attrs as uploaded image URLs (`iconSrc` + `iconAlt`):
// slash-menu commands upload the picked files to the same /files/upload
// endpoint used for inline images, then insert the block with the storage
// path. The public renderer resolves the path via toPublicAssetUrl().
import { Fragment } from '@tiptap/pm/model';
import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';

// Shared options shape: every block accepts an optional `view` so callers
// (the editor app) can attach a minimal NodeView with an Edit button. The
// public renderer ignores it entirely.
interface BlockOptions {
  HTMLAttributes: Record<string, any>;
  view: any;
}

const defaultBlockOptions = () => ({ HTMLAttributes: {}, view: null });

function maybeNodeView(self: { options: BlockOptions }) {
  if (!self.options.view) return null;
  return ReactNodeViewRenderer(self.options.view);
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    mappingTable: {
      insertMappingTable: (attrs?: {
        connector?: 'arrow' | 'double-arrow';
        rows?: number;
      }) => ReturnType;
    };
    iconChipGrid: {
      insertIconChipGrid: (attrs?: {
        columns?: 2 | 3 | 4 | 5 | 6;
        chips?: {
          iconSrc: string;
          iconAlt?: string;
          label?: string;
          href?: string | null;
          variant?: LayoutVariant;
        }[];
      }) => ReturnType;
    };
    inlineHighlight: {
      insertInlineHighlight: (attrs?: {
        iconSrc?: string | null;
        iconAlt?: string;
        text?: string;
        variant?: LayoutVariant;
      }) => ReturnType;
    };
    featureList: {
      insertFeatureList: (attrs?: {
        items?: {
          iconSrc?: string | null;
          iconAlt?: string;
          title?: string;
          badge?: string;
          text?: string;
          variant?: LayoutVariant;
        }[];
      }) => ReturnType;
    };
    titledFrame: {
      insertTitledFrame: (attrs?: { title?: string }) => ReturnType;
    };
    sectionDivider: {
      insertSectionDivider: (attrs?: { title?: string }) => ReturnType;
    };
    footerCallout: {
      insertFooterCallout: (attrs?: {
        iconSrc?: string | null;
        iconAlt?: string;
        text?: string;
        variant?: LayoutVariant;
      }) => ReturnType;
    };
  }
}

// Variant tokens map 1:1 to fumadocs semantic colors (fd-info / fd-warning
// / fd-success / fd-error / fd-idea / fd-accent / fd-primary). `default`
// uses the neutral muted palette. The public renderer is responsible for
// translating these into actual CSS classes — the editor-ext just stores
// the choice so swapping the design system is one map away.
export type LayoutVariant =
  | 'default'
  | 'primary'
  | 'accent'
  | 'info'
  | 'success'
  | 'warning'
  | 'error'
  | 'idea';

export const LAYOUT_VARIANTS: readonly LayoutVariant[] = [
  'default',
  'primary',
  'accent',
  'info',
  'success',
  'warning',
  'error',
  'idea',
];

const iconAttrs = () => ({
  iconSrc: {
    default: null as string | null,
    parseHTML: (el: HTMLElement) => el.getAttribute('data-icon-src'),
    renderHTML: (attrs: Record<string, any>) =>
      attrs.iconSrc ? { 'data-icon-src': attrs.iconSrc } : {},
  },
  iconAlt: {
    default: '',
    parseHTML: (el: HTMLElement) => el.getAttribute('data-icon-alt') ?? '',
    renderHTML: (attrs: Record<string, any>) =>
      attrs.iconAlt ? { 'data-icon-alt': attrs.iconAlt } : {},
  },
  variant: {
    default: 'default' as LayoutVariant,
    parseHTML: (el: HTMLElement) =>
      (el.getAttribute('data-variant') as LayoutVariant) ?? 'default',
    renderHTML: (attrs: Record<string, any>) =>
      attrs.variant && attrs.variant !== 'default'
        ? { 'data-variant': attrs.variant }
        : {},
  },
});

// ──────────────────────────────────────────────────────────────────────
// 1. Mapping table — two rich-content columns per row.
// ──────────────────────────────────────────────────────────────────────
export const MappingTable = Node.create<BlockOptions>({
  name: 'mappingTable',
  group: 'block',
  content: 'mappingRow+',
  defining: true,
  isolating: true,

  addOptions: defaultBlockOptions,
  addNodeView() {
    return maybeNodeView(this);
  },

  addAttributes() {
    return {
      connector: {
        default: 'arrow',
        parseHTML: (el) => el.getAttribute('data-connector') ?? 'arrow',
        renderHTML: (attrs) => ({ 'data-connector': attrs.connector }),
      },
    };
  },

  parseHTML() {
    return [{ tag: `div[data-type="${this.name}"]` }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes({ 'data-type': this.name }, HTMLAttributes), 0];
  },

  addCommands() {
    return {
      insertMappingTable:
        (attrs) =>
        ({ state, commands }) => {
          const rowType = state.schema.nodes.mappingRow;
          const cellType = state.schema.nodes.mappingCell;
          const paraType = state.schema.nodes.paragraph;
          if (!rowType || !cellType || !paraType) return false;
          const rows = Math.max(1, Math.min(20, attrs?.rows ?? 3));
          const makeRow = () =>
            rowType
              .create(
                null,
                Fragment.from([
                  cellType.create(null, paraType.create()),
                  cellType.create(null, paraType.create()),
                ]),
              )
              .toJSON();
          return commands.insertContent({
            type: this.name,
            attrs: { connector: attrs?.connector ?? 'arrow' },
            content: Array.from({ length: rows }, makeRow),
          });
        },
    };
  },
});

export const MappingRow = Node.create({
  name: 'mappingRow',
  group: 'mappingRow',
  content: 'mappingCell mappingCell',
  defining: true,
  isolating: true,

  parseHTML() {
    return [{ tag: `div[data-type="${this.name}"]` }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes({ 'data-type': this.name }, HTMLAttributes), 0];
  },
});

export const MappingCell = Node.create({
  name: 'mappingCell',
  group: 'mappingCell',
  content: 'block+',
  defining: true,
  isolating: true,
  selectable: false,

  parseHTML() {
    return [{ tag: `div[data-type="${this.name}"]` }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes({ 'data-type': this.name }, HTMLAttributes), 0];
  },
});

// ──────────────────────────────────────────────────────────────────────
// 2. Icon chip grid — uploaded icons + editable labels.
// ──────────────────────────────────────────────────────────────────────
export const IconChipGrid = Node.create<BlockOptions>({
  name: 'iconChipGrid',
  group: 'block',
  content: 'iconChip+',
  defining: true,
  isolating: true,

  addOptions: defaultBlockOptions,
  addNodeView() {
    return maybeNodeView(this);
  },

  addAttributes() {
    return {
      columns: {
        default: 6,
        parseHTML: (el) => {
          const n = parseInt(el.getAttribute('data-columns') ?? '6', 10);
          return n >= 2 && n <= 6 ? n : 6;
        },
        renderHTML: (attrs) => ({ 'data-columns': String(attrs.columns ?? 6) }),
      },
    };
  },

  parseHTML() {
    return [{ tag: `div[data-type="${this.name}"]` }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes({ 'data-type': this.name }, HTMLAttributes), 0];
  },

  addCommands() {
    return {
      insertIconChipGrid:
        (attrs) =>
        ({ commands }) => {
          const chipsIn = attrs?.chips ?? [];
          // No icons yet → seed a single empty-icon chip so the block isn't
          // unusable. Editor users would normally insert via the slash menu
          // which prompts for files first; this branch matters for tests
          // and programmatic insertion.
          const seeds = chipsIn.length
            ? chipsIn
            : [{ iconSrc: '', iconAlt: '', label: 'Label' }];
          const cols = (attrs?.columns ?? Math.max(2, Math.min(6, seeds.length))) as
            | 2
            | 3
            | 4
            | 5
            | 6;
          const content = seeds.map((c) => {
            // iconChip schema: `content: 'inline*'`. Wrapping the label in a
            // `paragraph` (a block) would fail validation and commands
            // .insertContent would silently return false. We push the text
            // node directly. Empty labels skip the text node entirely —
            // ProseMirror refuses empty text nodes.
            const label = (c.label ?? '').trim();
            return {
              type: 'iconChip',
              attrs: {
                iconSrc: c.iconSrc || null,
                iconAlt: c.iconAlt ?? '',
                href: (c as { href?: string | null }).href || null,
                variant:
                  (c as { variant?: LayoutVariant }).variant ?? 'default',
              },
              content: label ? [{ type: 'text', text: label }] : [],
            };
          });
          return commands.insertContent({
            type: this.name,
            attrs: { columns: cols },
            content,
          });
        },
    };
  },
});

export const IconChip = Node.create<BlockOptions>({
  name: 'iconChip',
  group: 'iconChip',
  content: 'inline*',
  defining: true,
  isolating: true,

  addOptions: defaultBlockOptions,
  addNodeView() {
    return maybeNodeView(this);
  },

  addAttributes() {
    return {
      ...iconAttrs(),
      href: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-href'),
        renderHTML: (attrs) => (attrs.href ? { 'data-href': attrs.href } : {}),
      },
    };
  },

  parseHTML() {
    return [{ tag: `div[data-type="${this.name}"]` }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes({ 'data-type': this.name }, HTMLAttributes), 0];
  },
});

// ──────────────────────────────────────────────────────────────────────
// 3. Inline highlight — single horizontal "icon + sentence" mini-card.
// ──────────────────────────────────────────────────────────────────────
export const InlineHighlight = Node.create<BlockOptions>({
  name: 'inlineHighlight',
  group: 'block',
  content: 'inline*',
  defining: true,

  addOptions: defaultBlockOptions,
  addNodeView() {
    return maybeNodeView(this);
  },

  addAttributes() {
    return iconAttrs();
  },

  parseHTML() {
    return [{ tag: `div[data-type="${this.name}"]` }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes({ 'data-type': this.name }, HTMLAttributes), 0];
  },

  addCommands() {
    return {
      insertInlineHighlight:
        (attrs) =>
        ({ commands }) => {
          const text = (attrs?.text ?? '').trim() || 'Highlight text';
          return commands.insertContent({
            type: this.name,
            attrs: {
              iconSrc: attrs?.iconSrc ?? null,
              iconAlt: attrs?.iconAlt ?? '',
              variant: attrs?.variant ?? 'default',
            },
            content: [{ type: 'text', text }],
          });
        },
    };
  },
});

// ──────────────────────────────────────────────────────────────────────
// 4. Feature list — vertical stack of icon | title + description + badge.
// ──────────────────────────────────────────────────────────────────────
export const FeatureList = Node.create<BlockOptions>({
  name: 'featureList',
  group: 'block',
  content: 'featureItem+',
  defining: true,
  isolating: true,

  addOptions: defaultBlockOptions,
  addNodeView() {
    return maybeNodeView(this);
  },

  parseHTML() {
    return [{ tag: `div[data-type="${this.name}"]` }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes({ 'data-type': this.name }, HTMLAttributes), 0];
  },

  addCommands() {
    return {
      insertFeatureList:
        (attrs) =>
        ({ commands }) => {
          const itemsIn =
            attrs?.items ??
            [{ title: 'Feature', badge: '', text: 'Describe the feature.' }];
          const content = itemsIn.map((it) => {
            // featureItem schema: `content: 'block+'`. A paragraph is a
            // valid block, but ProseMirror refuses empty text nodes — so
            // for empty descriptions we emit an empty paragraph (just no
            // text child) instead of `{type:'text', text:''}`.
            const text = (it.text ?? '').trim();
            const para = text
              ? { type: 'paragraph', content: [{ type: 'text', text }] }
              : { type: 'paragraph' };
            return {
              type: 'featureItem',
              attrs: {
                iconSrc: it.iconSrc || null,
                iconAlt: it.iconAlt ?? '',
                title: it.title ?? '',
                badge: it.badge ?? '',
                variant: it.variant ?? 'default',
              },
              content: [para],
            };
          });
          return commands.insertContent({ type: this.name, content });
        },
    };
  },
});

export const FeatureItem = Node.create<BlockOptions>({
  name: 'featureItem',
  group: 'featureItem',
  content: 'block+',
  defining: true,
  isolating: true,

  addOptions: defaultBlockOptions,
  addNodeView() {
    return maybeNodeView(this);
  },

  addAttributes() {
    return {
      ...iconAttrs(),
      title: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-title') ?? '',
        renderHTML: (attrs) => ({ 'data-title': attrs.title ?? '' }),
      },
      badge: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-badge') ?? '',
        renderHTML: (attrs) => ({ 'data-badge': attrs.badge ?? '' }),
      },
    };
  },

  parseHTML() {
    return [{ tag: `div[data-type="${this.name}"]` }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes({ 'data-type': this.name }, HTMLAttributes), 0];
  },
});

// ──────────────────────────────────────────────────────────────────────
// 5. Titled frame — bordered section with a centered title at the top.
// ──────────────────────────────────────────────────────────────────────
export const TitledFrame = Node.create<BlockOptions>({
  name: 'titledFrame',
  group: 'block',
  content: 'block+',
  defining: true,
  isolating: true,

  addOptions: defaultBlockOptions,
  addNodeView() {
    return maybeNodeView(this);
  },

  addAttributes() {
    return {
      title: {
        default: 'Section',
        parseHTML: (el) => el.getAttribute('data-title') ?? 'Section',
        renderHTML: (attrs) => ({ 'data-title': attrs.title ?? 'Section' }),
      },
    };
  },

  parseHTML() {
    return [{ tag: `div[data-type="${this.name}"]` }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes({ 'data-type': this.name }, HTMLAttributes), 0];
  },

  addCommands() {
    return {
      insertTitledFrame:
        (attrs) =>
        ({ commands }) =>
          commands.insertContent({
            type: this.name,
            attrs: { title: attrs?.title ?? 'Section' },
            content: [{ type: 'paragraph' }],
          }),
    };
  },
});

// ──────────────────────────────────────────────────────────────────────
// 6. Section divider — full-width heading with symmetric ornament strokes.
// ──────────────────────────────────────────────────────────────────────
export const SectionDivider = Node.create<BlockOptions>({
  name: 'sectionDivider',
  group: 'block',
  content: 'inline*',
  defining: true,

  addOptions: defaultBlockOptions,
  addNodeView() {
    return maybeNodeView(this);
  },

  parseHTML() {
    return [{ tag: `div[data-type="${this.name}"]` }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes({ 'data-type': this.name }, HTMLAttributes), 0];
  },

  addCommands() {
    return {
      insertSectionDivider:
        (attrs) =>
        ({ commands }) => {
          const title = (attrs?.title ?? '').trim() || 'Section';
          return commands.insertContent({
            type: this.name,
            content: [{ type: 'text', text: title }],
          });
        },
    };
  },
});

// ──────────────────────────────────────────────────────────────────────
// 7. Footer callout — narrow bottom strip with leading icon + one line.
// ──────────────────────────────────────────────────────────────────────
export const FooterCallout = Node.create<BlockOptions>({
  name: 'footerCallout',
  group: 'block',
  content: 'inline*',
  defining: true,

  addOptions: defaultBlockOptions,
  addNodeView() {
    return maybeNodeView(this);
  },

  addAttributes() {
    return iconAttrs();
  },

  parseHTML() {
    return [{ tag: `div[data-type="${this.name}"]` }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes({ 'data-type': this.name }, HTMLAttributes), 0];
  },

  addCommands() {
    return {
      insertFooterCallout:
        (attrs) =>
        ({ commands }) => {
          const text = (attrs?.text ?? '').trim() || 'Final note for the reader.';
          return commands.insertContent({
            type: this.name,
            attrs: {
              iconSrc: attrs?.iconSrc ?? null,
              iconAlt: attrs?.iconAlt ?? '',
              variant: attrs?.variant ?? 'default',
            },
            content: [{ type: 'text', text }],
          });
        },
    };
  },
});
